import { useState, useMemo, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import appConfig from '../config/app-config';
import useFetch from './useFetch';
import { collateralApi } from '../services/collateralApi';
import { calculateIntensity, type OperationType } from '../components/pages/collateral/collateralUtils';

const API = appConfig.apis.convertXmlApiEnv;

export interface OperationOption {
    id: string;
    creditOperationsId?: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    operationType: OperationType;
    obligation: string;
    traderName: string;
    collateralAnalysisId?: string;
    sentToValidation?: boolean;
}

interface PersonResult {
    uuid: string;
    name: string;
    alias: string | null;
}

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

export default function useVerificationConfigPageForm({ onCreated }: Props) {
    const [step, setStep] = useState<'select' | 'configure'>('select');
    const [selected, setSelected] = useState<OperationOption | null>(null);
    const [search, setSearch] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const [operationType, setOperationType] = useState('CESSAO');
    const [obligationType, setObligationType] = useState('WITHOUT_RECOURSE');
    const [traderName, setTraderName] = useState('');
    const [traderResults, setTraderResults] = useState<PersonResult[]>([]);
    const [showTraderDropdown, setShowTraderDropdown] = useState(false);
    const [isSearchingTrader, setIsSearchingTrader] = useState(false);

    const [validationConfig, setValidationConfig] = useState<Record<string, string>>({
        validation_xml: 'PRE_DISBURSEMENT',
        validation_invoice_pdf: 'DO_NOT_VALIDATE',
        validation_receipt_stub: 'DO_NOT_VALIDATE',
        validation_accepted_bill: 'DO_NOT_VALIDATE',
        validation_contact: 'DO_NOT_VALIDATE',
        validation_purchase_contract: 'DO_NOT_VALIDATE',
        validation_sefaz: 'DO_NOT_VALIDATE',
        validation_debtor_notification: 'DO_NOT_VALIDATE',
    });
    const [analysisCriteria, setAnalysisCriteria] = useState<'Completa' | 'Amostragem'>('Amostragem');
    const [sampledTitles, setSampledTitles] = useState(1);
    const [debtorNotificationRequested, setDebtorNotificationRequested] = useState(false);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API.key || '' },
        { key: 'Accept-Charset', value: 'utf-8' },
    ], []);
    const { isLoading, data } = useFetch(`${API.address}/operations?page=0&pageSize=50`, fetchHeaders);

    const operations: OperationOption[] = useMemo(() => {
        if (!data?.list) return [];
        return data.list.filter((o: OperationOption) => !o.collateralAnalysisId);
    }, [data]);

    const filteredOps = useMemo(() => {
        if (!search) return operations;
        const s = search.toLowerCase();
        return operations.filter(o => o.operationCode?.toLowerCase().includes(s) || o.assignorName?.toLowerCase().includes(s));
    }, [operations, search]);

    const intensity = useMemo(() => {
        if (!selected) return null;
        const e = (k: string) => validationConfig[k] !== 'DO_NOT_VALIDATE';
        return calculateIntensity(
            { xml: e('validation_xml'), nf: e('validation_invoice_pdf'), canhoto: e('validation_receipt_stub'), duplicata: e('validation_accepted_bill') },
            selected.totalReceivables || 0
        );
    }, [selected, validationConfig]);

    const searchTrader = useCallback(async (query: string) => {
        if (query.length < 3) { setTraderResults([]); setShowTraderDropdown(false); return; }
        setIsSearchingTrader(true);
        try {
            const results = await collateralApi.searchLegalPerson(query);
            setTraderResults(results);
            setShowTraderDropdown(true);
        } catch { /* ignore */ }
        finally { setIsSearchingTrader(false); }
    }, []);

    const handleSelectTrader = (person: PersonResult) => {
        setTraderName(person.name);
        setShowTraderDropdown(false);
    };

    const handleSelectOperation = (op: OperationOption) => {
        setSelected(op);
        setOperationType(op.operationType || 'CESSAO');
        setObligationType(op.obligation || 'WITHOUT_RECOURSE');
        setTraderName(op.traderName || '');
    };

    const handleCreate = async () => {
        if (!selected) return;
        setIsCreating(true);
        try {
            const fetchHeaders: Record<string, string> = { 'x-api-key': API.key || '' };
            const session = await Auth.currentSession();
            fetchHeaders['Authorization'] = `Bearer ${session.getIdToken().getJwtToken()}`;

            const opRes = await fetch(`${API.address}/operations/${selected.id}`, { headers: fetchHeaders });
            const opData = await opRes.json();
            const documents = opData?.documents || [];
            const receivable_ids = documents.flatMap((d: any) => (d.receivables || []).map((r: any) => r.receivable_id)).filter(Boolean);
            const receivables = documents.flatMap((d: any) =>
                (d.receivables || []).map((r: any) => ({
                    receivable_id: r.receivable_id,
                    nfe_key: d.document_id || '',
                    title_number: d.document_title || '',
                    receivable_number: r.receivable_number || '',
                    debtor_cpf_cnpj: d.debtor_cpf_cnpj || '',
                    debtor_name: d.debtor_name || '',
                    debtor_email: d.debtor_email || '',
                    debtor_phone: d.address_phone || '',
                    amount: r.amount || 0,
                    due_date: r.due_date || '',
                }))
            ).filter((r: any) => r.receivable_id);

            const enabledCriteria: { criteria_type: string; timing: string }[] = [];
            const criteriaMap: Record<string, string> = {
                validation_xml: 'XML', validation_invoice_pdf: 'INVOICE_PDF',
                validation_receipt_stub: 'RECEIPT_STUB', validation_contact: 'DRAWEE_CONFIRMATION',
                validation_sefaz: 'SEFAZ', validation_accepted_bill: 'ACCEPTED_BILL',
                validation_purchase_contract: 'PURCHASE_CONTRACT', validation_debtor_notification: 'DEBTOR_NOTIFICATION',
            };
            for (const [key, type] of Object.entries(criteriaMap)) {
                const val = validationConfig[key];
                if (val && val !== 'DO_NOT_VALIDATE' && val !== 'false') {
                    enabledCriteria.push({ criteria_type: type, timing: val === 'true' ? 'PRE_DISBURSEMENT' : val });
                }
            }

            await collateralApi.createVerificationConfig({
                credit_operation_id: selected.creditOperationsId || selected.id,
                criteria: enabledCriteria,
                receivable_ids, receivables,
                analysis_mode: analysisCriteria === 'Amostragem' ? 'SAMPLE' : 'COMPLETE',
                sample_count: analysisCriteria === 'Amostragem' ? sampledTitles : undefined,
                operation_code: selected.operationCode,
                operation_type: operationType,
                obligation_type: obligationType,
                assignor_name: selected.assignorName || '',
                assignor_tax_id: selected.assignorCnpj || '',
                total_receivables: selected.totalReceivables || 0,
                total_amount: selected.totalAmount || 0,
                trader_name: traderName,
            });

            onCreated();
        } catch (e: any) {
            if (e?.status === 409) toast.warn('Análise já existe para esta operação.');
            else toast.error('Erro ao criar análise. Tente novamente.');
        } finally { setIsCreating(false); }
    };

    return {
        step, setStep, selected, search, setSearch, isLoading, isCreating,
        operationType, setOperationType, obligationType, setObligationType,
        traderName, setTraderName, traderResults, showTraderDropdown, isSearchingTrader,
        validationConfig, setValidationConfig,
        analysisCriteria, setAnalysisCriteria, sampledTitles, setSampledTitles,
        debtorNotificationRequested, setDebtorNotificationRequested,
        operations, filteredOps, intensity,
        searchTrader, handleSelectTrader, handleSelectOperation, handleCreate,
    };
}
